import type { ThemeSettings } from '~/stores/auth'
import { hexToRgb } from '~/composables/useLayoutStyles'

export function useCardHover(themeSettings: Ref<ThemeSettings> | ComputedRef<ThemeSettings>) {
  const hoverType = computed(() => themeSettings.value.cardHover ?? 'none')

  function onCardMouseMove(e: MouseEvent) {
    if (hoverType.value === 'none') return
    const el = e.currentTarget as HTMLElement

    // Cancel the entrance animation (card-* keyframes) so transforms work.
    // Cancel instead of setting `animation: none`: Vue re-applies the inline
    // animation style on every re-render, which would replay the entrance.
    for (const animation of el.getAnimations()) {
      if (animation instanceof CSSAnimation && animation.animationName.startsWith('card-')) {
        animation.cancel()
      }
    }

    // Save original box-shadow on first hover
    if (!el.dataset.hoverInit) {
      el.dataset.hoverInit = '1'
      el.dataset.originalShadow = el.style.boxShadow
    }

    const rect = el.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    const centerX = rect.width / 2
    const centerY = rect.height / 2
    const rgb = hexToRgb(themeSettings.value.primaryColor)
    const type = hoverType.value

    if (type === 'tilt') {
      const rotateX = ((y - centerY) / centerY) * -8
      const rotateY = ((x - centerX) / centerX) * 8
      el.style.transform = `perspective(800px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`
      el.style.transition = 'transform 0.1s ease-out, box-shadow 0.25s ease-out'
      el.style.boxShadow = `0 20px 40px rgba(0,0,0,0.3), 0 0 20px rgba(${rgb.join(',')},0.15)`
    } else if (type === 'lift') {
      el.style.transform = 'translateY(-8px)'
      el.style.transition = 'transform 0.25s ease-out, box-shadow 0.25s ease-out'
      el.style.boxShadow = `0 20px 40px rgba(0,0,0,0.3), 0 0 30px rgba(${rgb.join(',')},0.15)`
    } else if (type === 'glow') {
      el.style.transition = 'box-shadow 0.25s ease-out'
      el.style.boxShadow = `0 0 20px rgba(${rgb.join(',')},0.3), 0 0 40px rgba(${rgb.join(',')},0.15), 0 8px 32px rgba(0,0,0,0.2)`
    } else if (type === 'scale') {
      el.style.transform = 'scale(1.03)'
      el.style.transition = 'transform 0.25s ease-out, box-shadow 0.25s ease-out'
      el.style.boxShadow = `0 15px 35px rgba(0,0,0,0.25), 0 0 20px rgba(${rgb.join(',')},0.1)`
    }
  }

  function onCardMouseLeave(e: MouseEvent) {
    if (hoverType.value === 'none') return
    const el = e.currentTarget as HTMLElement
    el.style.transform = ''
    el.style.boxShadow = el.dataset.originalShadow || ''
    el.style.transition = 'transform 0.3s ease-out, box-shadow 0.3s ease-out'
  }

  return {
    hoverType,
    onCardMouseMove,
    onCardMouseLeave
  }
}
